import { useCallback, useEffect, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { Position } from '@/types/geometry';
import { DRAG_CLICK_THRESHOLD_PX } from '@/constants/notes';
import { useLatest } from './useLatest';

export interface DragDelta {
  readonly dx: number;
  readonly dy: number;
}

export interface DragState<C = unknown> {
  readonly start: Position;
  readonly current: Position;
  readonly delta: DragDelta;
  readonly cache: C;
  readonly pointerEvent: PointerEvent;
  readonly target: HTMLElement;
}

export interface UseDragOptions<C> {
  /** Runs on pointerdown. Return value is cached and passed to onDrag/onEnd. */
  readonly onStart?: (event: ReactPointerEvent<HTMLElement>) => C;
  /** Fires on every rAF-throttled pointermove during the gesture. */
  readonly onDrag?: (state: DragState<C>) => void;
  /**
   * Fires on pointerup. `distance` lets consumers distinguish click vs drag —
   * callers may treat drags below `clickThreshold` as clicks.
   */
  readonly onEnd?: (state: DragState<C> & { readonly distance: number }) => void;
  /** Fires when the user presses Escape or pointer capture is lost. */
  readonly onCancel?: (cache: C) => void;
  /** Pixel threshold below which we treat the gesture as a click. Default 4. */
  readonly clickThreshold?: number;
  /** Button filter (0 = primary). Default 0. */
  readonly button?: number;
  readonly disabled?: boolean;
}

interface Internal<C> {
  active: boolean;
  cache: C | null;
  target: HTMLElement | null;
  pointerId: number | null;
  start: Position;
  latestPointerEvent: PointerEvent | null;
  rafId: number | null;
}

/**
 * The core pointer-drag primitive powering move, resize, and creation.
 *
 * Design points:
 *  - PointerEvents (unified mouse/touch/pen; desktop-only spec but no reason not to).
 *  - Pointer capture on the target element so we keep receiving events even if
 *    the pointer leaves it.
 *  - `pointermove` is batched into a single rAF per frame → 60fps without
 *    dropping information.
 *  - `user-select: none` on <body> during drag to prevent text selection.
 *  - Escape cancels; component unmount cancels.
 */
export function useDrag<C = void>(
  options: UseDragOptions<C>,
): (event: ReactPointerEvent<HTMLElement>) => void {
  const optsRef = useLatest(options);
  const internalRef = useRef<Internal<C>>({
    active: false,
    cache: null,
    target: null,
    pointerId: null,
    start: { x: 0, y: 0 },
    latestPointerEvent: null,
    rafId: null,
  });

  const endGesture = useCallback((cancelled: boolean, finalEvent: PointerEvent | null) => {
    const s = internalRef.current;
    if (!s.active) return;

    if (s.rafId !== null) {
      cancelAnimationFrame(s.rafId);
      s.rafId = null;
    }

    const target = s.target;
    if (target && s.pointerId !== null) {
      try {
        target.releasePointerCapture(s.pointerId);
      } catch {
        /* ignore */
      }
    }
    document.body.style.userSelect = '';

    const opts = optsRef.current;
    const ev = finalEvent ?? s.latestPointerEvent;
    const cache = s.cache as C;

    if (cancelled) {
      opts.onCancel?.(cache);
    } else if (ev && target) {
      const currentPos: Position = { x: ev.clientX, y: ev.clientY };
      const dx = currentPos.x - s.start.x;
      const dy = currentPos.y - s.start.y;
      const distance = Math.hypot(dx, dy);
      opts.onEnd?.({
        start: s.start,
        current: currentPos,
        delta: { dx, dy },
        cache,
        pointerEvent: ev,
        target,
        distance,
      });
    }

    window.removeEventListener('pointermove', handleWindowPointerMove);
    window.removeEventListener('pointerup', handleWindowPointerUp);
    window.removeEventListener('pointercancel', handleWindowPointerCancel);
    document.removeEventListener('keydown', handleKeyDown);

    s.active = false;
    s.cache = null;
    s.target = null;
    s.pointerId = null;
    s.latestPointerEvent = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flushRaf = useCallback(() => {
    const s = internalRef.current;
    s.rafId = null;
    const ev = s.latestPointerEvent;
    if (!s.active || !ev || !s.target) return;
    const opts = optsRef.current;
    const currentPos: Position = { x: ev.clientX, y: ev.clientY };
    const dx = currentPos.x - s.start.x;
    const dy = currentPos.y - s.start.y;
    opts.onDrag?.({
      start: s.start,
      current: currentPos,
      delta: { dx, dy },
      cache: s.cache as C,
      pointerEvent: ev,
      target: s.target,
    });
  }, [optsRef]);

  const handleWindowPointerMove = useCallback(
    (event: PointerEvent) => {
      const s = internalRef.current;
      if (!s.active) return;
      s.latestPointerEvent = event;
      if (s.rafId === null) {
        s.rafId = requestAnimationFrame(flushRaf);
      }
    },
    [flushRaf],
  );

  const handleWindowPointerUp = useCallback(
    (event: PointerEvent) => {
      const s = internalRef.current;
      if (!s.active || s.pointerId !== event.pointerId) return;
      s.latestPointerEvent = event;
      endGesture(false, event);
    },
    [endGesture],
  );

  const handleWindowPointerCancel = useCallback(() => {
    endGesture(true, null);
  }, [endGesture]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') endGesture(true, null);
    },
    [endGesture],
  );

  useEffect(() => {
    const stateRef = internalRef;
    return () => {
      if (stateRef.current.active) endGesture(true, null);
    };
  }, [endGesture]);

  return useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const opts = optsRef.current;
      if (opts.disabled) return;
      const button = opts.button ?? 0;
      if (event.button !== button) return;

      event.preventDefault();

      const target = event.currentTarget;
      const s = internalRef.current;
      const cache = opts.onStart?.(event);
      s.active = true;
      s.cache = (cache ?? null) as C | null;
      s.target = target;
      s.pointerId = event.pointerId;
      s.start = { x: event.clientX, y: event.clientY };
      s.latestPointerEvent = event.nativeEvent;

      try {
        target.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
      document.body.style.userSelect = 'none';

      window.addEventListener('pointermove', handleWindowPointerMove);
      window.addEventListener('pointerup', handleWindowPointerUp);
      window.addEventListener('pointercancel', handleWindowPointerCancel);
      document.addEventListener('keydown', handleKeyDown);
    },
    [
      optsRef,
      handleWindowPointerMove,
      handleWindowPointerUp,
      handleWindowPointerCancel,
      handleKeyDown,
    ],
  );
}

/** Utility for callers who want click-vs-drag decisioning without threshold config. */
export function wasClick(distance: number, threshold = DRAG_CLICK_THRESHOLD_PX): boolean {
  return distance < threshold;
}
